/**
 * Standalone Aithoris Consent JS SDK (IIFE → window.AithorisConsent).
 * Built to FE/public/sdk/aithoris-consent.js
 */
import {
  COMPALLY_CONSENT_SDK_VERSION,
  ConsentSdkClient,
  needsReconsent,
  readConsentCache,
} from "./consent-sdk-client";
import type {
  ConsentSdkBannerOptions,
  ConsentSdkConsentState,
  ConsentSdkEventHandler,
  ConsentSdkEventName,
  ConsentSdkPolicy,
  ConsentSdkPosition,
  ConsentSdkTheme,
} from "./consent-sdk.types";
import { mountPrivacyDashboard } from "./privacy-dashboard-mount";
import { getConsentUiString, getLanguageDisplayName } from "./ui-strings.ts";
import { applyTranslationOverlay, readySelectorLanguages } from "./apply-translation.ts";
import {
  activateGatedScripts,
  deleteDeniedCookies,
  grantedSet,
  initialVisitorGrants,
  normalizeVisitorState,
  rememberNeedsPrompt,
  resolveThemeMode,
} from "./consent-behavior.ts";

export interface AithorisConsentInitOptions {
  /** Public Site ID (`cmp_live_…`). Prefer this over appId. */
  siteId?: string;
  /** @deprecated Prefer siteId — kept for older embeds. */
  appId?: string;
  principalId?: string;
  apiBaseUrl?: string;
  /**
   * Host that serves `/consent/rights-request/…` (Aithoris app, not the merchant site).
   * Defaults to the SDK script origin, then `data-app-base`, then production app host.
   */
  appBaseUrl?: string;
  position?: ConsentSdkPosition;
  theme?: "light" | "dark" | ConsentSdkTheme;
  autoShow?: boolean;
  /** Preferred consent notice language (published translations only). */
  language?: string;
  allowAnonymous?: boolean;
  primaryColor?: string;
  logoUrl?: string;
  onEvent?: ConsentSdkEventHandler;
  /**
   * Admin preview: always shows the notice, ignores stored visitor consent and never records,
   * withdraws, stores or gates anything.
   */
  preview?: boolean;
  /** Unsaved branding applied on top of the published theme (preview only). */
  previewTheme?: ConsentSdkTheme;
  /** Unsaved banner options (buttons, footer, animation…) merged over the published ones. */
  previewBannerOptions?: ConsentSdkBannerOptions;
}

export type AithorisConsentPreview = {
  theme?: ConsentSdkTheme | null;
  bannerOptions?: ConsentSdkBannerOptions | null;
};

function mergeBannerOptions(
  base: ConsentSdkBannerOptions | undefined,
  over: ConsentSdkBannerOptions | null,
): ConsentSdkBannerOptions | undefined {
  if (!over) return base;
  return {
    ...(base ?? {}),
    ...over,
    buttons: { ...(base?.buttons ?? {}), ...(over.buttons ?? {}) },
    footer: { ...(base?.footer ?? {}), ...(over.footer ?? {}) },
  };
}

/** @deprecated Use AithorisConsentInitOptions */
export type CompallyConsentInitOptions = AithorisConsentInitOptions;

type Instance = {
  showNotice: () => void;
  /** Open Preferences directly (works after Accept All). */
  showPreferences: () => void;
  /** Open the hosted Data Rights Request form in a new tab. */
  openDataRightsRequest: (opts?: { type?: string; email?: string }) => void;
  hide: () => void;
  getConsent: () => Promise<ConsentSdkConsentState | null>;
  withdraw: () => Promise<void>;
  checkVersion: () => Promise<{ needsUi: boolean; latestVersion: string | null }>;
  mergeAnonymous: (principalId: string) => Promise<unknown>;
  destroy: () => void;
  /** C13 */
  requestOtp: (principalId: string, identifierType?: string) => Promise<unknown>;
  loginWithOtp: (principalId: string, otp: string, identifierType?: string) => Promise<unknown>;
  logoutPrivacy: () => void;
  getPrivacyDashboard: () => Promise<unknown>;
  getConsentHistory: () => Promise<unknown>;
  getActivityTimeline: () => Promise<unknown>;
  getRequests: () => Promise<unknown>;
  getRequestStatus: (id: string) => Promise<unknown>;
  updatePreferences: (body: Parameters<ConsentSdkClient["updatePreferences"]>[0]) => Promise<unknown>;
  withdrawConsent: (recordId?: string) => Promise<unknown>;
  requestAccountDeletion: (body?: { subject?: string; description?: string }) => Promise<unknown>;
  requestDataExport: (body?: { subject?: string; description?: string }) => Promise<unknown>;
  raiseComplaint: (body: { subject: string; description: string; type?: string }) => Promise<unknown>;
  requestCorrection: (body?: { subject?: string; description?: string }) => Promise<unknown>;
  mountPrivacyDashboard: (
    el: HTMLElement,
    opts?: {
      principalId?: string;
      identifierType?: string;
      primaryColor?: string;
      theme?: import("./privacy-dashboard-mount").PrivacyDashboardTheme;
      onClose?: () => void;
    },
  ) => { unmount: () => void; refresh: () => Promise<void> };
  unmountPrivacyDashboard: () => void;
  /** Preview only: re-render with unsaved branding / banner options. */
  setPreview: (next: AithorisConsentPreview) => void;
};

let privacyMount: { unmount: () => void; refresh: () => Promise<void> } | null = null;
let dataRightsLauncher: HTMLElement | null = null;

let active: Instance | null = null;
const listeners = new Set<ConsentSdkEventHandler>();

const AITHORIS_APP_FALLBACK = "https://app.aithoris.com";

function resolveAppBase(explicit?: string): string {
  if (explicit?.trim()) return explicit.trim().replace(/\/$/, "");
  const script = findBootScript();
  const fromAttr = script?.getAttribute("data-app-base")?.trim();
  if (fromAttr) return fromAttr.replace(/\/$/, "");
  if (script?.src) {
    try {
      return new URL(script.src, window.location.href).origin.replace(/\/$/, "");
    } catch {
      /* ignore */
    }
  }
  const tagged = document.querySelector<HTMLScriptElement>(
    "script[data-site-id][src*='aithoris-consent'], script[data-site-id][src*='compally-consent']",
  );
  if (tagged?.src) {
    try {
      return new URL(tagged.src, window.location.href).origin.replace(/\/$/, "");
    } catch {
      /* ignore */
    }
  }
  return AITHORIS_APP_FALLBACK;
}

function buildRightsRequestUrl(
  appBaseUrl: string,
  siteOrAppId: string,
  opts?: { type?: string; email?: string; device?: { principalId: string; recordId: string } | null },
): string {
  const q = new URLSearchParams();
  if (opts?.type) q.set("type", opts.type);
  if (opts?.email) q.set("email", opts.email);
  const qs = q.toString();
  // In the fragment so the browser's consent proof never reaches server logs or referrers.
  const hash = opts?.device
    ? `#${new URLSearchParams({ pid: opts.device.principalId, rid: opts.device.recordId }).toString()}`
    : "";
  return `${appBaseUrl.replace(/\/$/, "")}/consent/rights-request/${encodeURIComponent(siteOrAppId)}${qs ? `?${qs}` : ""}${hash}`;
}

function removeDataRightsLauncher() {
  if (dataRightsLauncher?.parentNode) dataRightsLauncher.parentNode.removeChild(dataRightsLauncher);
  dataRightsLauncher = null;
}

function mountDataRightsLauncher(opts: {
  label: string;
  hint: string;
  primaryColor: string;
  onClick: () => void;
}) {
  removeDataRightsLauncher();
  const btn = document.createElement("button");
  btn.type = "button";
  btn.id = "aithoris-data-rights-launcher";
  btn.setAttribute("aria-label", opts.label);
  btn.title = opts.hint;
  btn.textContent = opts.label;
  const primary = cssEsc(opts.primaryColor || "#4B5FE8");
  btn.style.cssText = `
    position:fixed;z-index:2147482990;right:16px;bottom:16px;max-width:min(280px,calc(100vw - 32px));
    border:0;border-radius:999px;padding:12px 18px;background:${primary};color:#fff;
    font:650 13px/1.3 ui-sans-serif,system-ui,-apple-system,Segoe UI,sans-serif;
    box-shadow:0 10px 28px rgba(15,23,42,.28);cursor:pointer;text-align:center;
  `;
  btn.addEventListener("click", opts.onClick);
  document.body.appendChild(btn);
  dataRightsLauncher = btn;
}

let preferencesLauncher: HTMLElement | null = null;

function removePreferencesLauncher() {
  if (preferencesLauncher?.parentNode) preferencesLauncher.parentNode.removeChild(preferencesLauncher);
  preferencesLauncher = null;
}

function mountPreferencesLauncher(opts: { label: string; primaryColor: string; onClick: () => void }) {
  removePreferencesLauncher();
  const btn = document.createElement("button");
  btn.type = "button";
  btn.id = "aithoris-consent-preferences-launcher";
  btn.setAttribute("aria-label", opts.label);
  btn.title = opts.label;
  btn.textContent = `🍪 ${opts.label}`;
  const primary = cssEsc(opts.primaryColor || "#4B5FE8");
  btn.style.cssText = `
    position:fixed;z-index:2147482990;left:16px;bottom:16px;max-width:min(240px,calc(100vw - 32px));
    border:1px solid rgba(148,163,184,.35);border-radius:999px;padding:10px 16px;background:#fff;color:${primary};
    font:650 13px/1.3 ui-sans-serif,system-ui,-apple-system,Segoe UI,sans-serif;
    box-shadow:0 10px 28px rgba(15,23,42,.18);cursor:pointer;text-align:center;
  `;
  btn.addEventListener("click", opts.onClick);
  document.body.appendChild(btn);
  preferencesLauncher = btn;
}

function prefersDarkScheme(): boolean {
  try {
    return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches === true;
  } catch {
    return false;
  }
}

function prefersReducedMotion(): boolean {
  try {
    return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  } catch {
    return false;
  }
}

function langStorageKey(appId: string): string {
  return `aithoris_consent_lang:${appId}`;
}

function emit(event: ConsentSdkEventName, payload?: Record<string, unknown>) {
  for (const fn of listeners) {
    try {
      fn(event, payload);
    } catch {
      /* ignore listener errors */
    }
  }
}

function resolveApiBase(explicit?: string): string {
  if (explicit?.trim()) return explicit.trim().replace(/\/$/, "");
  const script = document.currentScript as HTMLScriptElement | null;
  const fromAttr = script?.getAttribute("data-api-base")?.trim();
  if (fromAttr) return fromAttr.replace(/\/$/, "");
  const tagged = document.querySelector<HTMLScriptElement>(
    "script[data-site-id][data-api-base], script[data-aithoris-consent][data-api-base], script[data-compally-consent][data-api-base], script[src*='aithoris-consent'][data-api-base], script[src*='compally-consent'][data-api-base]",
  );
  const fromTagged = tagged?.getAttribute("data-api-base")?.trim();
  if (fromTagged) return fromTagged.replace(/\/$/, "");
  // Same-origin when SDK is hosted beside the API (or Vite proxy).
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/$/, "");
  }
  throw new Error("AithorisConsent.init requires apiBaseUrl (or script[data-api-base])");
}

function findBootScript(): HTMLScriptElement | null {
  if (document.currentScript instanceof HTMLScriptElement) {
    return document.currentScript;
  }
  return document.querySelector<HTMLScriptElement>(
    "script[data-site-id][src*='aithoris-consent'], script[data-site-id][src*='compally-consent'], script[data-site-id][src*='Aithoris-consent']",
  );
}

function normalizeTheme(
  theme?: "light" | "dark" | ConsentSdkTheme,
  extras?: Partial<ConsentSdkTheme>,
): ConsentSdkTheme {
  const base: ConsentSdkTheme =
    typeof theme === "string" ? { theme } : { ...(theme ?? {}) };
  return { ...base, ...extras };
}

function cssEsc(v: string): string {
  return v.replace(/[<>"']/g, "");
}

function buildUi(
  root: HTMLElement,
  policy: ConsentSdkPolicy,
  theme: ConsentSdkTheme,
  position: ConsentSdkPosition,
  grants: Record<string, boolean>,
  principalId: string,
  handlers: {
    onAcceptAll: (grants?: Record<string, boolean>, isAdult?: boolean) => void;
    onRejectOptional: () => void;
    onSave: (grants: Record<string, boolean>, isAdult?: boolean) => void;
    onWithdraw: () => void;
    onClose: () => void;
    onPrincipalChange: (v: string) => void;
    onLanguageChange?: (lang: string, mode: "banner" | "preferences") => void;
    onOpenDataRights?: () => void;
    /** Lets the caller re-render into the same view (e.g. when translations arrive). */
    onModeChange?: (mode: "banner" | "preferences") => void;
  },
  opts?: { initialMode?: "banner" | "preferences"; appBaseUrl?: string; animate?: boolean },
) {
  const lang = policy.languageSelected || "en";
  const t = (key: Parameters<typeof getConsentUiString>[1]) => getConsentUiString(lang, key);
  const bo = policy.bannerOptions ?? null;
  const catalog = policy.cookieCatalog ?? null;
  // Admin-typed labels / CTA are English; other languages use the curated SDK strings.
  const label = (key: Parameters<typeof getConsentUiString>[1], override?: string) =>
    lang === "en" && override?.trim() ? override.trim() : t(key);
  const primary = cssEsc(theme.primaryColor || "#4B5FE8");
  const acceptColor = cssEsc(bo?.buttons?.acceptColor?.trim() || theme.primaryColor || "#4B5FE8");
  const rejectColor = bo?.buttons?.rejectColor?.trim() ? cssEsc(bo.buttons.rejectColor.trim()) : "";
  const bg = cssEsc(theme.backgroundColor || (theme.theme === "dark" ? "#0f172a" : "#ffffff"));
  const text = cssEsc(theme.textColor || (theme.theme === "dark" ? "#f8fafc" : "#0f172a"));
  const muted = theme.theme === "dark" ? "rgba(248,250,252,.7)" : "#64748b";
  const line = theme.theme === "dark" ? "rgba(148,163,184,.25)" : "#e2e8f0";
  const isBottom = position === "bottom";
  const isTop = position === "top";
  const fontSize = Math.min(20, Math.max(12, Number(theme.fontSize) || 14));
  const boxRadius = Math.min(28, Math.max(0, Number(theme.boxRadius) || 16));
  const boxPadding = Math.min(28, Math.max(10, Number(theme.boxPadding) || 20));
  const boxMax = Math.min(920, Math.max(280, Number(theme.boxMaxWidth) || 920));
  const btnRadius =
    theme.buttonStyle === "pill" ? "999px" : theme.buttonStyle === "square" ? "4px" : "10px";
  const localGrants = { ...grants };
  for (const p of policy.purposes ?? []) {
    if (localGrants[p.id] === undefined) localGrants[p.id] = Boolean(p.required);
  }

  let mode: "banner" | "preferences" = opts?.initialMode ?? "banner";
  let openedFromBanner = (opts?.initialMode ?? "banner") === "banner";
  /** Keeps the Preferences list where it was when a toggle re-renders the modal. */
  let prefsScrollTop = 0;
  const setMode = (next: "banner" | "preferences") => {
    if (next !== mode) prefsScrollTop = 0;
    if (mode === "banner" && next === "preferences") {
      openedFromBanner = true;
    }
    mode = next;
    handlers.onModeChange?.(next);
  };
  let animated = !opts?.animate || !bo?.animation || bo.animation === "none" || prefersReducedMotion();
  const appBase = (opts?.appBaseUrl || resolveAppBase()).replace(/\/$/, "");

  const mkBtn = (label: string, kind: "primary" | "ghost" | "danger", onClick: () => void) => {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = label;
    if (kind === "primary") {
      b.style.cssText = `border:0;border-radius:${btnRadius};padding:12px 18px;background:${acceptColor};color:#fff;font-weight:700;cursor:pointer;font-size:${fontSize}px;`;
    } else if (kind === "danger") {
      b.style.cssText = `border:1px solid #fecaca;border-radius:${btnRadius};padding:12px 18px;background:#fff5f5;color:#b91c1c;font-weight:650;cursor:pointer;font-size:${fontSize}px;`;
    } else {
      b.style.cssText = `border:1px solid ${rejectColor || line};border-radius:${btnRadius};padding:12px 18px;background:transparent;color:${rejectColor || "inherit"};font-weight:650;cursor:pointer;font-size:${fontSize}px;`;
    }
    b.addEventListener("click", onClick);
    return b;
  };

  const render = () => {
    root.innerHTML = "";
    root.style.cssText = `
      position:fixed;inset:0;z-index:2147483000;display:flex;
      align-items:${isBottom ? "flex-end" : isTop ? "flex-start" : "center"};justify-content:center;
      background:rgba(15,23,42,.42);padding:16px;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,sans-serif;
      pointer-events:auto;
    `;

    // --- Banner strip (always the same card; Preferences opens a separate modal) ---
    const card = document.createElement("div");
    card.setAttribute("role", "dialog");
    card.setAttribute("aria-modal", "true");
    card.setAttribute("aria-label", "Consent banner");
    card.style.cssText =
      isBottom || isTop
        ? `width:min(${boxMax}px,100%);border-radius:${boxRadius}px;background:${bg};color:${text};box-shadow:0 24px 60px rgba(15,23,42,.28);padding:${boxPadding}px;border:1px solid ${line};`
        : `width:100%;max-width:${Math.min(boxMax, 480)}px;border-radius:${boxRadius}px;background:${bg};color:${text};box-shadow:0 24px 60px rgba(15,23,42,.28);padding:${boxPadding}px;border:1px solid ${line};`;

    if (theme.logoUrl) {
      const logoH = Math.min(72, Math.max(16, Number(theme.logoHeight) || 28));
      const logoAlign =
        theme.logoAlign === "center" || theme.logoAlign === "right" ? theme.logoAlign : "left";
      const beside = theme.logoPlacement === "beside";
      const img = document.createElement("img");
      img.src = theme.logoUrl;
      img.alt = "";
      img.style.cssText = `height:${logoH}px;width:auto;max-width:${beside ? 96 : 160}px;object-fit:contain;border-radius:4px;flex-shrink:0;`;

      if (beside) {
        const row = document.createElement("div");
        row.style.cssText = `display:flex;align-items:flex-start;gap:10px;margin-bottom:8px;justify-content:${
          logoAlign === "center" ? "center" : logoAlign === "right" ? "flex-end" : "flex-start"
        };${logoAlign === "right" ? "flex-direction:row-reverse;" : ""}`;
        row.appendChild(img);
        const textCol = document.createElement("div");
        textCol.style.cssText = "min-width:0;flex:1;";
        row.appendChild(textCol);
        card.appendChild(row);
        (card as HTMLElement & { __logoTextCol?: HTMLElement }).__logoTextCol = textCol;
      } else {
        const wrap = document.createElement("div");
        wrap.style.cssText = `display:flex;justify-content:${
          logoAlign === "center" ? "center" : logoAlign === "right" ? "flex-end" : "flex-start"
        };margin-bottom:10px;`;
        wrap.appendChild(img);
        card.appendChild(wrap);
      }
    }

    const titleParent =
      (card as HTMLElement & { __logoTextCol?: HTMLElement }).__logoTextCol ?? card;

    // Ready-only: never list admin-enabled languages that lack a translation object.
    const selectorLangs = readySelectorLanguages(policy.localization);
    const multilingualOn = Boolean(policy.localization?.multilingualEnabled);
    const showLangSelect =
      multilingualOn && selectorLangs.length > 1 && Boolean(handlers.onLanguageChange);

    const eyebrow = document.createElement("p");
    eyebrow.textContent = policy.app.name;
    eyebrow.style.cssText = `margin:0;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:${primary};`;
    titleParent.appendChild(eyebrow);

    const titleRow = document.createElement("div");
    titleRow.style.cssText =
      "display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin:6px 0 0;";
    const title = document.createElement("h2");
    title.textContent = policy.title || theme.bannerHeadline || t("weUseCookies");
    title.style.cssText = `margin:0;min-width:0;flex:1;font-size:${fontSize + 6}px;font-weight:750;letter-spacing:-.02em;color:${primary};`;
    titleRow.appendChild(title);

    const makeLangSelect = (): HTMLElement => {
      const langWrap = document.createElement("label");
      langWrap.style.cssText = "position:relative;display:inline-flex;flex-shrink:0;margin-top:2px;";
      const select = document.createElement("select");
      select.setAttribute("aria-label", t("language"));
      select.title = t("language");
      select.style.cssText = `appearance:none;-webkit-appearance:none;font-size:12px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;border:1px solid ${line};border-radius:8px;padding:6px 26px 6px 10px;background:transparent;color:inherit;cursor:pointer;`;
      for (const code of selectorLangs) {
        const opt = document.createElement("option");
        opt.value = code;
        opt.textContent = getLanguageDisplayName(code);
        if (code === lang) opt.selected = true;
        select.appendChild(opt);
      }
      select.addEventListener("change", () => {
        handlers.onLanguageChange?.(select.value, mode);
      });
      const caret = document.createElement("span");
      caret.setAttribute("aria-hidden", "true");
      caret.textContent = "▾";
      caret.style.cssText =
        "position:absolute;right:8px;top:50%;transform:translateY(-50%);font-size:10px;pointer-events:none;opacity:.7;";
      langWrap.appendChild(select);
      langWrap.appendChild(caret);
      return langWrap;
    };

    if (showLangSelect) {
      titleRow.appendChild(makeLangSelect());
    }
    titleParent.appendChild(titleRow);

    const notice = document.createElement("p");
    notice.textContent =
      policy.notice ||
      theme.bannerBody ||
      "Choose how we may use your data. You can change this anytime.";
    notice.style.cssText = `margin:8px 0 0;font-size:${fontSize}px;line-height:1.5;color:${muted};`;
    titleParent.appendChild(notice);

    if (lang === "en" && bo?.ctaHighlight?.trim()) {
      const cta = document.createElement("p");
      cta.textContent = bo.ctaHighlight.trim();
      cta.style.cssText = `margin:8px 0 0;font-size:${fontSize}px;font-weight:700;color:${acceptColor};`;
      titleParent.appendChild(cta);
    }

    const childrenWarningEnabled = Boolean(
      bo?.childrenWarningEnabled ?? theme.childrenWarningEnabled
    );

    const adultStorageKey = `aithoris_adult_${policy.app.id}`;
    let isConfirmedAdult = !childrenWarningEnabled;
    try {
      if (childrenWarningEnabled && typeof localStorage !== "undefined") {
        const stored = localStorage.getItem(adultStorageKey);
        if (stored === "1") isConfirmedAdult = true;
        else if (stored === "0") isConfirmedAdult = false;
      }
    } catch {
      /* ignore */
    }

    if (childrenWarningEnabled) {
      const gateLabel = document.createElement("label");
      gateLabel.style.cssText = `margin-top:12px;padding:8px 12px;background:color-mix(in srgb, ${acceptColor} 6%, transparent);border-radius:8px;display:flex;gap:10px;align-items:center;cursor:pointer;font-size:${Math.max(12, fontSize - 1)}px;font-weight:600;color:${text};user-select:none;border:1px solid color-mix(in srgb, ${acceptColor} 18%, transparent);`;

      const gateBox = document.createElement("input");
      gateBox.type = "checkbox";
      gateBox.checked = isConfirmedAdult;
      gateBox.style.cssText = `width:16px;height:16px;cursor:pointer;accent-color:${acceptColor};margin:0;`;
      gateBox.addEventListener("change", () => {
        isConfirmedAdult = gateBox.checked;
        if (!isConfirmedAdult) {
          for (const p of policy.purposes ?? []) {
            if (!p.required) localGrants[p.id] = false;
          }
        }
      });

      const gateText = document.createElement("span");
      gateText.textContent = t("ageGateLabel");

      gateLabel.appendChild(gateBox);
      gateLabel.appendChild(gateText);
      titleParent.appendChild(gateLabel);
    }

    const actions = document.createElement("div");
    actions.style.cssText =
      "margin-top:16px;display:flex;flex-wrap:wrap;gap:8px;align-items:center;justify-content:flex-end;";
    actions.appendChild(
      mkBtn(label("acceptAll", bo?.buttons?.acceptLabel), "primary", () => {
        if (childrenWarningEnabled && !isConfirmedAdult) {
          // Section 9(3): Under-18 visitors cannot have ad tracking or optional profiling cookies
          for (const p of policy.purposes ?? []) {
            localGrants[p.id] = Boolean(p.required);
          }
          alert(t("ageGateMinorNotice"));
        } else {
          for (const p of policy.purposes ?? []) localGrants[p.id] = true;
        }
        handlers.onAcceptAll({ ...localGrants }, isConfirmedAdult);
      }),
    );
    actions.appendChild(
      mkBtn(label("rejectAll", bo?.buttons?.rejectLabel), "ghost", () => {
        for (const p of policy.purposes ?? []) localGrants[p.id] = Boolean(p.required);
        handlers.onRejectOptional();
      }),
    );
    const prefsLink = document.createElement("button");
    prefsLink.type = "button";
    prefsLink.textContent = label("preferences", bo?.buttons?.customizeLabel);
    prefsLink.style.cssText = `border:0;background:transparent;color:inherit;font-weight:700;cursor:pointer;font-size:${fontSize}px;padding:8px 4px;text-decoration:underline;`;
    prefsLink.addEventListener("click", () => {
      setMode("preferences");
      render();
    });
    actions.appendChild(prefsLink);
    card.appendChild(actions);

    const foot = document.createElement("p");
    foot.style.cssText = `margin:14px 0 0;display:flex;flex-wrap:wrap;gap:12px;font-size:12px;color:${muted};`;
    const privacyHref =
      (typeof theme.privacyPolicyUrl === "string" && theme.privacyPolicyUrl.trim()) ||
      bo?.footer?.privacyPolicyUrl?.trim() ||
      "";
    const cookieHref =
      (typeof theme.cookiePolicyUrl === "string" && theme.cookiePolicyUrl.trim()) || "";
    if (privacyHref) {
      const privacy = document.createElement("a");
      privacy.href = privacyHref;
      privacy.target = "_blank";
      privacy.rel = "noopener noreferrer";
      privacy.textContent = t("privacyPolicy");
      privacy.style.cssText = `color:inherit;font-weight:650;text-decoration:underline;`;
      foot.appendChild(privacy);
    }
    if (cookieHref) {
      const cookie = document.createElement("a");
      cookie.href = cookieHref;
      cookie.target = "_blank";
      cookie.rel = "noopener noreferrer";
      cookie.textContent = t("cookiePolicy");
      cookie.style.cssText = `color:inherit;font-weight:650;text-decoration:underline;`;
      foot.appendChild(cookie);
    }
    const extraLinks: Array<[string, string | undefined]> = [
      [t("terms"), bo?.footer?.termsUrl],
      [t("dpo"), bo?.footer?.dpoUrl],
      [t("contact"), bo?.footer?.contactUrl],
    ];
    for (const [text, href] of extraLinks) {
      if (!href?.trim()) continue;
      const a = document.createElement("a");
      a.href = href.trim();
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.textContent = text;
      a.style.cssText = `color:inherit;font-weight:650;text-decoration:underline;`;
      foot.appendChild(a);
    }
    if (foot.childNodes.length) card.appendChild(foot);

    if (mode === "banner") {
      root.appendChild(card);

      if (!animated) {
        animated = true;
        const anim = bo?.animation;
        card.style.transition = "transform .28s ease, opacity .28s ease";
        card.style.opacity = "0";
        if (anim === "slide") card.style.transform = isTop ? "translateY(-18px)" : "translateY(18px)";
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            card.style.opacity = "1";
            card.style.transform = "none";
          }),
        );
      }
    }

    // --- Separate Preferences modal (new layer, replaces banner when active) ---
    if (mode === "preferences") {
      const overlay = document.createElement("div");
      overlay.style.cssText =
        "position:fixed;inset:0;z-index:2147483010;display:flex;align-items:center;justify-content:center;background:rgba(15,23,42,.55);padding:16px;";
      const handleDismissPreferences = () => {
        if (openedFromBanner) {
          setMode("banner");
          render();
        } else {
          handlers.onClose();
        }
      };

      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) {
          handleDismissPreferences();
        }
      });

      const modal = document.createElement("div");
      modal.setAttribute("role", "dialog");
      modal.setAttribute("aria-modal", "true");
      modal.setAttribute("aria-label", "Preferences");
      modal.style.cssText = `position:relative;width:100%;max-width:760px;max-height:min(88vh,820px);display:flex;flex-direction:column;overflow:hidden;border-radius:16px;background:${bg};color:${text};box-shadow:0 24px 60px rgba(15,23,42,.4);border:1px solid ${line};box-sizing:border-box;`;
      const wide = typeof window !== "undefined" && window.innerWidth >= 720;
      const padX = wide ? 24 : 16;
      const header = document.createElement("div");
      header.style.cssText = `position:relative;flex-shrink:0;padding:18px ${padX}px 12px;border-bottom:1px solid ${line};`;
      const body = document.createElement("div");
      body.style.cssText = `flex:1;min-height:0;overflow-y:auto;overscroll-behavior:contain;padding:14px ${padX}px 16px;`;
      body.addEventListener("scroll", () => {
        prefsScrollTop = body.scrollTop;
      });
      const footer = document.createElement("div");
      footer.style.cssText = `flex-shrink:0;display:flex;flex-wrap:wrap;gap:8px;justify-content:flex-end;align-items:center;padding:12px ${padX}px;border-top:1px solid ${line};background:${bg};`;

      const closeBtn = document.createElement("button");
      closeBtn.type = "button";
      closeBtn.setAttribute("aria-label", "Close preferences");
      closeBtn.textContent = "×";
      closeBtn.style.cssText =
        "position:absolute;right:12px;top:12px;border:0;background:transparent;font-size:22px;line-height:1;cursor:pointer;color:inherit;opacity:.55;";
      closeBtn.addEventListener("click", handleDismissPreferences);
      header.appendChild(closeBtn);

      const prefsHead = document.createElement("div");
      prefsHead.style.cssText =
        "display:flex;align-items:center;justify-content:space-between;gap:10px;margin-right:32px;";
      const prefsTitle = document.createElement("h2");
      prefsTitle.textContent = t("preferences");
      prefsTitle.style.cssText = `margin:0;font-size:${fontSize + 8}px;font-weight:750;color:${primary};`;
      prefsHead.appendChild(prefsTitle);
      // Visitors can switch language from Preferences modal.
      if (showLangSelect) prefsHead.appendChild(makeLangSelect());
      header.appendChild(prefsHead);

      const prefsBody = document.createElement("p");
      prefsBody.textContent =
        policy.notice ||
        theme.bannerBody ||
        "Manage your consent preferences. Required purposes cannot be disabled.";
      prefsBody.style.cssText = `margin:8px 0 0;font-size:${fontSize}px;line-height:1.5;color:${muted};`;
      (wide ? header : body).appendChild(prefsBody);

      const idLabel = document.createElement("label");
      idLabel.style.cssText = `display:block;max-width:${wide ? "360px" : "none"};margin-top:${wide ? "0" : "14px"};font-size:11px;font-weight:700;text-transform:uppercase;color:${muted};`;
      idLabel.textContent = t("yourIdOptional");
      const idInput = document.createElement("input");
      idInput.type = "text";
      idInput.value = principalId;
      idInput.placeholder = "you@example.com";
      idInput.style.cssText = `width:100%;margin-top:6px;padding:10px 12px;border-radius:10px;border:1px solid ${line};box-sizing:border-box;background:transparent;color:inherit;`;
      idInput.addEventListener("input", () => handlers.onPrincipalChange(idInput.value));
      idLabel.appendChild(idInput);
      body.appendChild(idLabel);

      const list = document.createElement("div");
      list.style.cssText = `margin-top:14px;display:grid;gap:10px;grid-template-columns:${wide ? "repeat(2,minmax(0,1fr))" : "1fr"};align-items:start;`;

      if (childrenWarningEnabled) {
        const prefGate = document.createElement("label");
        prefGate.style.cssText = `display:flex;gap:10px;align-items:center;margin-top:14px;padding:10px 14px;background:color-mix(in srgb, ${primary} 6%, transparent);border-radius:10px;cursor:pointer;font-size:${Math.max(12, fontSize - 1)}px;font-weight:600;color:${text};user-select:none;border:1px solid color-mix(in srgb, ${primary} 20%, transparent);`;

        const prefBox = document.createElement("input");
        prefBox.type = "checkbox";
        prefBox.checked = isConfirmedAdult;
        prefBox.style.cssText = `width:16px;height:16px;cursor:pointer;accent-color:${primary};margin:0;`;
        prefBox.addEventListener("change", () => {
          isConfirmedAdult = prefBox.checked;
          if (!isConfirmedAdult) {
            for (const p of policy.purposes ?? []) {
              if (!p.required) localGrants[p.id] = false;
            }
          }
          render();
        });

        const prefText = document.createElement("span");
        prefText.textContent = t("ageGateLabel");

        prefGate.appendChild(prefBox);
        prefGate.appendChild(prefText);
        body.appendChild(prefGate);
      }

      for (const p of policy.purposes ?? []) {
        const row = document.createElement("div");
        row.style.cssText = `display:flex;gap:12px;align-items:flex-start;padding:14px;border:1px solid ${line};border-radius:12px;box-sizing:border-box;min-width:0;`;

        const iconWrap = document.createElement("span");
        iconWrap.setAttribute("aria-hidden", "true");
        iconWrap.style.cssText =
          "width:36px;height:36px;border-radius:12px;background:rgba(148,163,184,.15);display:grid;place-items:center;font-size:18px;flex-shrink:0;";
        const iconRaw = typeof p.icon === "string" ? p.icon.trim() : "";
        if (/^https?:\/\//i.test(iconRaw) || iconRaw.startsWith("data:image")) {
          const img = document.createElement("img");
          img.src = iconRaw;
          img.alt = "";
          img.style.cssText = "width:36px;height:36px;border-radius:12px;object-fit:cover;";
          img.onerror = () => {
            iconWrap.textContent = p.required ? "🛡️" : "📌";
          };
          iconWrap.appendChild(img);
        } else {
          iconWrap.textContent =
            iconRaw ||
            (p.required || /essential|necessary/i.test(p.name)
              ? "🛡️"
              : /analy|stat/i.test(p.name)
                ? "📊"
                : /market|ad/i.test(p.name)
                  ? "📣"
                  : /preferen|setting/i.test(p.name)
                    ? "⚙️"
                    : "📌");
        }
        row.appendChild(iconWrap);

        const textWrap = document.createElement("div");
        textWrap.style.cssText = "min-width:0;flex:1;";
        const nameRow = document.createElement("div");
        nameRow.style.cssText =
          "display:flex;align-items:center;justify-content:space-between;gap:12px;";
        const name = document.createElement("strong");
        name.textContent = p.name;
        name.style.cssText = "display:block;font-size:14px;";
        nameRow.appendChild(name);
        if (p.required) {
          const badge = document.createElement("span");
          badge.textContent = t("alwaysActive");
          badge.style.cssText = `font-size:11px;font-weight:650;color:${muted};white-space:nowrap;background:rgba(148,163,184,.15);border-radius:999px;padding:4px 10px;`;
          nameRow.appendChild(badge);
        } else {
          const isRestrictedMinor = childrenWarningEnabled && !isConfirmedAdult;
          const on = isRestrictedMinor ? false : Boolean(localGrants[p.id]);
          const toggle = document.createElement("button");
          toggle.type = "button";
          toggle.setAttribute("aria-pressed", on ? "true" : "false");
          toggle.setAttribute("aria-label", `Toggle ${p.name}`);
          toggle.style.cssText = `width:44px;height:26px;border-radius:999px;border:0;position:relative;cursor:pointer;background:${on ? primary : line};flex-shrink:0;${isRestrictedMinor ? "opacity:.65;" : ""}`;
          const knob = document.createElement("span");
          knob.style.cssText = `position:absolute;top:3px;left:${on ? "21px" : "3px"};width:20px;height:20px;border-radius:999px;background:#fff;transition:left .15s;`;
          toggle.appendChild(knob);
          toggle.addEventListener("click", () => {
            if (isRestrictedMinor) {
              alert(t("ageGateMinorNotice"));
              return;
            }
            localGrants[p.id] = !localGrants[p.id];
            render();
          });
          nameRow.appendChild(toggle);
        }
        textWrap.appendChild(nameRow);
        if (p.description) {
          const d = document.createElement("span");
          d.textContent = p.description;
          d.style.cssText = `display:block;font-size:12px;color:${muted};margin-top:6px;line-height:1.45;`;
          textWrap.appendChild(d);
        }
        const purposeCookies = (catalog?.categories ?? [])
          .filter((c) => c.purposeId === p.id && c.showInPreferenceCenter)
          .flatMap((c) => c.cookies ?? []);
        if (purposeCookies.length) {
          const det = document.createElement("details");
          det.style.cssText = `margin-top:8px;font-size:12px;color:${muted};`;
          const sum = document.createElement("summary");
          sum.textContent = `${t("cookiesUsed")} (${purposeCookies.length})`;
          sum.style.cssText = "cursor:pointer;font-weight:650;";
          det.appendChild(sum);
          const ul = document.createElement("ul");
          ul.style.cssText = "margin:6px 0 0;padding-left:16px;line-height:1.5;";
          for (const ck of purposeCookies) {
            const li = document.createElement("li");
            li.textContent = [ck.name, ck.provider, ck.duration].filter(Boolean).join(" · ");
            if (ck.purpose || ck.description) li.title = ck.purpose || ck.description || "";
            ul.appendChild(li);
          }
          det.appendChild(ul);
          textWrap.appendChild(det);
        }
        row.appendChild(textWrap);
        list.appendChild(row);
      }
      body.appendChild(list);

      const officerName =
        typeof theme.grievanceOfficerName === "string" ? theme.grievanceOfficerName.trim() : "";
      const officerEmail =
        (typeof theme.grievanceOfficerEmail === "string" && theme.grievanceOfficerEmail.trim()) ||
        (typeof theme.contactEmail === "string" && theme.contactEmail.trim()) ||
        policy.app.email ||
        "";
      if (officerName || officerEmail) {
        const officer = document.createElement("p");
        officer.style.cssText = `margin:12px 0 0;font-size:12px;color:${muted};`;
        officer.textContent = `${t("grievanceOfficer")}${officerName ? `: ${officerName}` : ""}${
          officerEmail ? `${officerName ? " — " : ": "}${officerEmail}` : ""
        }`;
        body.appendChild(officer);
      }

      const prefsActions = footer;
      prefsActions.appendChild(
        mkBtn(label("rejectAll", bo?.buttons?.rejectLabel), "ghost", () => {
          for (const p of policy.purposes ?? []) localGrants[p.id] = Boolean(p.required);
          handlers.onRejectOptional();
        }),
      );
      prefsActions.appendChild(
        mkBtn(label("acceptAll", bo?.buttons?.acceptLabel), "ghost", () => {
          if (childrenWarningEnabled && !isConfirmedAdult) {
            for (const p of policy.purposes ?? []) localGrants[p.id] = Boolean(p.required);
            alert(t("ageGateMinorNotice"));
          } else {
            for (const p of policy.purposes ?? []) localGrants[p.id] = true;
          }
          handlers.onAcceptAll({ ...localGrants }, isConfirmedAdult);
        }),
      );
      prefsActions.appendChild(
        mkBtn(label("savePreferences", bo?.buttons?.saveLabel), "primary", () => {
          if (childrenWarningEnabled && !isConfirmedAdult) {
            for (const p of policy.purposes ?? []) {
              if (!p.required) localGrants[p.id] = false;
            }
          }
          handlers.onSave({ ...localGrants }, isConfirmedAdult);
        }),
      );

      const extras = document.createElement("div");
      extras.style.cssText = `margin-top:14px;display:grid;gap:10px;grid-template-columns:${wide ? "repeat(2,minmax(0,1fr))" : "1fr"};`;
      const revoke = mkBtn(t("revokeAll"), "danger", () => handlers.onWithdraw());
      revoke.style.width = "100%";
      extras.appendChild(revoke);

      const rights = document.createElement("button");
      rights.type = "button";
      rights.style.cssText = `display:block;width:100%;text-align:left;border:1px solid ${line};border-radius:12px;padding:12px 14px;background:rgba(148,163,184,.08);cursor:pointer;color:inherit;`;
      const rightsTitle = document.createElement("strong");
      rightsTitle.style.cssText = "display:block;font-size:13px";
      rightsTitle.textContent = t("dataRightsRequest");
      const rightsHint = document.createElement("span");
      rightsHint.style.cssText = `display:block;margin-top:4px;font-size:11px;color:${muted}`;
      rightsHint.textContent = t("dataRightsRequestHint");
      rights.appendChild(rightsTitle);
      rights.appendChild(rightsHint);
      rights.addEventListener("click", () => {
        if (handlers.onOpenDataRights) {
          handlers.onOpenDataRights();
          return;
        }
        const email = principalId.includes("@") ? principalId : undefined;
        window.open(
          buildRightsRequestUrl(appBase, policy.app.id, { email }),
          "_blank",
          "noopener,noreferrer",
        );
      });
      extras.appendChild(rights);
      body.appendChild(extras);

      const secured = document.createElement("p");
      secured.textContent = `${t("securedBy")} Aithoris`;
      secured.style.cssText = `margin:12px 0 0;text-align:center;font-size:10px;color:${muted};`;
      body.appendChild(secured);
      modal.appendChild(header);
      modal.appendChild(body);
      modal.appendChild(footer);

      overlay.appendChild(modal);
      root.appendChild(overlay);
      const scrollBody = modal.children[1] as HTMLElement | undefined;
      if (scrollBody && prefsScrollTop) {
        const top = prefsScrollTop;
        requestAnimationFrame(() => {
          scrollBody.scrollTop = top;
        });
      }
    }
  };

  render();
}

async function init(options: AithorisConsentInitOptions): Promise<Instance> {
  if (active) active.destroy();

  const apiBaseUrl = resolveApiBase(options.apiBaseUrl);
  const appId = (options.siteId ?? options.appId)?.trim();
  if (!appId) throw new Error("AithorisConsent.init requires siteId (or appId)");
  const appBaseUrl = resolveAppBase(options.appBaseUrl);

  if (options.onEvent) listeners.add(options.onEvent);

  const client = new ConsentSdkClient({
    apiBaseUrl,
    appId,
    sdkVersion: COMPALLY_CONSENT_SDK_VERSION,
  });

  const preview = options.preview === true;
  let previewTheme: ConsentSdkTheme | null = preview ? (options.previewTheme ?? null) : null;
  let previewBanner: ConsentSdkBannerOptions | null = preview
    ? (options.previewBannerOptions ?? null)
    : null;
  /** Published bannerOptions, kept so preview overrides can be re-applied or cleared. */
  let publishedBanner: ConsentSdkBannerOptions | undefined;
  let publishedPosition: ConsentSdkPosition = "bottom";
  const applyPreviewBanner = () => {
    if (!masterPolicy) return;
    masterPolicy = { ...masterPolicy, bannerOptions: mergeBannerOptions(publishedBanner, previewBanner) };
    policy = applyTranslationOverlay(masterPolicy, selectedLanguage || "en");
    const p = masterPolicy.bannerOptions?.position;
    position = p === "top" || p === "bottom" || p === "center" ? p : publishedPosition;
  };
  let principalId = preview
    ? ""
    : options.principalId?.trim() || client.getStoredAnonymousPrincipal() || "";
  let policy: ConsentSdkPolicy | null = null;
  let activeConsent: ConsentSdkConsentState | null = null;
  let root: HTMLDivElement | null = null;
  let selectedLanguage = options.language?.trim() || "";
  /** Last known purpose choice (any policy version) — used by `remember` visitor state. */
  let priorGrants: Record<string, boolean> | null = null;
  /** Full config with all published translations — language switch is client-side only. */
  let masterPolicy: ConsentSdkPolicy | null = null;

  const theme = normalizeTheme(options.theme, {
    position: options.position,
    primaryColor: options.primaryColor,
    logoUrl: options.logoUrl,
  });
  let position = theme.position ?? options.position ?? "bottom";
  let currentMode: "banner" | "preferences" = "banner";
  let colorSchemeQuery: MediaQueryList | null = null;
  let onColorSchemeChange: (() => void) | null = null;

  const hide = () => {
    if (root?.parentNode) root.parentNode.removeChild(root);
    root = null;
    emit("hidden");
    ensureDataRightsLauncher();
    ensurePreferencesLauncher();
  };

  /** Effective grants → activate consent-controlled scripts, expire denied catalog cookies. */
  const applyGrants = (grants: Record<string, boolean>, reason: string) => {
    if (preview) return;
    const granted = grantedSet(grants);
    const catalog = masterPolicy?.cookieCatalog ?? policy?.cookieCatalog ?? null;
    try {
      activateGatedScripts(document, granted, catalog);
      deleteDeniedCookies(document, catalog, granted);
    } catch {
      /* never break the host page */
    }
    emit("consentChanged", { purposes: grants, reason });
  };

  const preferencesLauncherEnabled = () => {
    const merged = { ...theme, ...policy?.theme, ...previewTheme } as ConsentSdkTheme;
    if (merged.showPreferencesLauncher === false) return false;
    return (
      merged.showPreferencesLauncher === true ||
      Boolean(masterPolicy?.bannerOptions) ||
      merged.showManagePreferences !== false
    );
  };

  const ensurePreferencesLauncher = () => {
    if (!activeConsent || !preferencesLauncherEnabled()) {
      removePreferencesLauncher();
      return;
    }
    const lang = selectedLanguage || policy?.languageSelected || "en";
    const merged = { ...theme, ...policy?.theme, ...previewTheme } as ConsentSdkTheme;
    mountPreferencesLauncher({
      label: getConsentUiString(lang, "manageConsent"),
      primaryColor: merged.primaryColor || "#4B5FE8",
      onClick: () => showPreferences(),
    });
  };

  const openDataRightsRequest = (opts?: { type?: string; email?: string }) => {
    const email =
      opts?.email ||
      (principalId.includes("@") ? principalId : undefined) ||
      undefined;
    const siteKey = policy?.app.id || appId;
    const device =
      activeConsent?.recordId && (activeConsent.principalId || principalId)
        ? { principalId: activeConsent.principalId || principalId, recordId: activeConsent.recordId }
        : null;
    window.open(
      buildRightsRequestUrl(appBaseUrl, siteKey, { type: opts?.type, email, device }),
      "_blank",
      "noopener,noreferrer",
    );
  };

  const ensureDataRightsLauncher = () => {
    // Branding from init options, notice copy from current language overlay.
    const merged = { ...theme, ...policy?.theme, ...previewTheme } as ConsentSdkTheme & {
      showDataRightsLauncher?: boolean;
    };
    if (merged.showDataRightsLauncher === false) {
      removeDataRightsLauncher();
      return;
    }
    const lang = selectedLanguage || policy?.languageSelected || "en";
    mountDataRightsLauncher({
      label: getConsentUiString(lang, "dataRightsRequest"),
      hint: getConsentUiString(lang, "dataRightsRequestHint"),
      primaryColor: merged.primaryColor || theme.primaryColor || "#4B5FE8",
      onClick: () => openDataRightsRequest(),
    });
  };

  /** Theme for UI: init branding first, then translated policy.theme so language switches update copy. */
  const themeForUi = (): ConsentSdkTheme => {
    const merged: ConsentSdkTheme = { ...theme, ...(policy?.theme ?? {}) };
    const boTheme = masterPolicy?.bannerOptions?.theme;
    if (!boTheme) return previewTheme ? { ...merged, ...previewTheme } : merged;
    const mode = resolveThemeMode(boTheme, prefersDarkScheme());
    const lightDefaults = new Set(["#fff", "#ffffff", "#0f172a", ""]);
    const out: ConsentSdkTheme = { ...merged, theme: mode };
    if (mode === "dark") {
      if (lightDefaults.has(String(merged.backgroundColor ?? "").toLowerCase())) out.backgroundColor = "#0f172a";
      if (lightDefaults.has(String(merged.textColor ?? "").toLowerCase())) out.textColor = "#f8fafc";
    }
    return previewTheme ? { ...out, ...previewTheme } : out;
  };

  const applyLanguageToPolicy = (lang: string, mode: "banner" | "preferences" = "banner") => {
    selectedLanguage = lang;
    if (!preview) {
      try {
        localStorage.setItem(langStorageKey(appId), lang);
      } catch {
        /* storage may be blocked */
      }
    }
    if (!masterPolicy) return;
    policy = applyTranslationOverlay(masterPolicy, lang);
    showNotice(mode, { animate: false });
  };

  let translationPollTimer: ReturnType<typeof setInterval> | null = null;
  const stopTranslationCatchupPoll = () => {
    if (translationPollTimer) {
      clearInterval(translationPollTimer);
      translationPollTimer = null;
    }
  };

  /**
   * Optional catch-up: while admin-enabled langs are still preparing, refresh the
   * config so newly PUBLISHED languages appear in the selector. Language switching
   * itself never waits on this — only ready langs are selectable.
   */
  const startTranslationCatchupPoll = () => {
    stopTranslationCatchupPoll();
    const loc = masterPolicy?.localization;
    if (!loc?.multilingualEnabled) return;
    const enabled = (loc.enabledLanguages ?? []).filter(Boolean);
    if (enabled.length <= 1) return;
    const published = new Set(
      Object.keys(loc.translations ?? {}).map((l) => l.toLowerCase()),
    );
    const stillPending = () =>
      enabled.some((l) => l !== "en" && !published.has(l.toLowerCase()));
    if (!stillPending()) return;

    let attempts = 0;
    translationPollTimer = setInterval(() => {
      void (async () => {
        attempts += 1;
        if (attempts > 45) {
          stopTranslationCatchupPoll();
          return;
        }
        try {
          const next = await client.getConfig();
          const nextLoc = next.localization;
          const nextPublished = Object.keys(nextLoc?.translations ?? {}).map((c) => c.toLowerCase());
          // Nothing new is ready: don't touch the open banner / preferences.
          if (nextPublished.every((c) => published.has(c)) && nextPublished.length === published.size) {
            return;
          }
          masterPolicy = next;
          published.clear();
          for (const code of nextPublished) published.add(code);
          // Keep current selection if still ready; otherwise English.
          const keep = selectedLanguage || "en";
          const keepReady =
            keep === "en" || Boolean(nextLoc?.translations?.[keep.toLowerCase()]);
          const nextLang = keepReady ? keep : "en";
          selectedLanguage = nextLang;
          policy = applyTranslationOverlay(next, nextLang);
          publishedBanner = next.bannerOptions ?? undefined;
          if (previewBanner) applyPreviewBanner();
          if (root) showNotice(currentMode, { animate: false });
          else ensureDataRightsLauncher();
          if (!stillPending()) stopTranslationCatchupPoll();
        } catch {
          /* ignore transient poll errors */
        }
      })();
    }, 4000);
  };

  const visitorGrants = () =>
    initialVisitorGrants({
      purposes: policy?.purposes ?? [],
      visitorState: normalizeVisitorState(
        masterPolicy?.bannerOptions?.defaultVisitorState ?? theme.defaultVisitorState,
      ),
      catalog: masterPolicy?.cookieCatalog ?? null,
      prior: priorGrants,
    });

  const showNotice = (
    initialMode: "banner" | "preferences" = "banner",
    showOpts?: { animate?: boolean },
  ) => {
    if (!policy) return;
    if (root?.parentNode) root.parentNode.removeChild(root);
    root = null;
    removeDataRightsLauncher();
    removePreferencesLauncher();
    currentMode = initialMode;
    root = document.createElement("div");
    root.id = "aithoris-consent-root";
    const adultKey = `aithoris_adult_${policy.app.id}`;
    let storedAdult: boolean | null = null;
    try {
      if (typeof localStorage !== "undefined") {
        const v = localStorage.getItem(adultKey);
        if (v === "1") storedAdult = true;
        else if (v === "0") storedAdult = false;
      }
    } catch {
      /* ignore */
    }
    const childrenWarningOn = Boolean(
      masterPolicy?.bannerOptions?.childrenWarningEnabled ?? theme.childrenWarningEnabled
    );
    const isMinor = childrenWarningOn && storedAdult === false;

    const grants: Record<string, boolean> = {};
    const fallback = activeConsent ? null : visitorGrants().uiGrants;
    for (const p of policy.purposes ?? []) {
      if (isMinor && !p.required) {
        grants[p.id] = false;
      } else {
        grants[p.id] =
          activeConsent?.purposes?.[p.id] ?? fallback?.[p.id] ?? Boolean(p.required);
      }
    }
    buildUi(
      root,
      policy,
      themeForUi(),
      previewTheme?.position ?? position,
      grants,
      principalId,
      {
        onPrincipalChange: (v) => {
          principalId = v;
        },
        onAcceptAll: (overrideGrants, isAdult) => {
          const toSave =
            overrideGrants ??
            Object.fromEntries((policy!.purposes ?? []).map((p) => [p.id, true]));
          void save(toSave, isAdult);
        },
        onRejectOptional: () => {
          void save(
            Object.fromEntries(
              (policy!.purposes ?? []).map((p) => [p.id, Boolean(p.required)]),
            ),
            false,
          );
        },
        onSave: (g, isAdult) => void save(g, isAdult),
        onWithdraw: () => void withdraw(),
        onClose: hide,
        onLanguageChange: (lang, mode) => {
          const code = (lang || "en").toLowerCase();
          const hasTx =
            code === "en" ||
            Boolean(masterPolicy?.localization?.translations?.[code]);
          // Local switch only — never start a translation or claim an unready language.
          if (hasTx) {
            applyLanguageToPolicy(code, mode);
          }
        },
        onOpenDataRights: () => openDataRightsRequest(),
        onModeChange: (m) => {
          currentMode = m;
        },
      },
      { initialMode, appBaseUrl, animate: showOpts?.animate ?? true },
    );
    document.body.appendChild(root);
    emit("shown");
  };

  const showPreferences = () => showNotice("preferences");

  const save = async (grants: Record<string, boolean>, isAdult?: boolean) => {
    if (!policy) return;
    try {
      if (typeof localStorage !== "undefined" && isAdult !== undefined) {
        localStorage.setItem(`aithoris_adult_${policy.app.id}`, isAdult ? "1" : "0");
      }
    } catch {
      /* ignore */
    }
    if (preview) {
      emit("accepted", { preview: true, purposes: grants });
      hide();
      return;
    }
    const dataPointConsents = (policy.purposes ?? []).map((p) => ({
      dataPointId: p.id,
      consentGranted: Boolean(grants[p.id]),
      purposeAgreedTo: p.name,
    }));
    const allGranted = dataPointConsents.every((d) => d.consentGranted);
    const noneGranted = dataPointConsents.every((d) => !d.consentGranted);
    try {
      const state = await client.recordConsent({
        principalId: principalId || undefined,
        anonymous: !principalId && (options.allowAnonymous ?? true),
        policyId: policy.policyId,
        policyVersion: policy.version,
        consentStatusGeneral: allGranted ? "granted" : noneGranted ? "denied" : "custom",
        consentMechanism: "aithoris_js_sdk",
        userAgent: navigator.userAgent,
        languageSelected: policy.languageSelected || selectedLanguage || "en",
        dataPointConsents,
      });
      principalId = state.principalId;
      activeConsent = state;
      priorGrants = { ...grants };
      emit("accepted", { consent: state });
      applyGrants(state.purposes ?? grants, "save");
      hide();
    } catch (e) {
      emit("error", { message: e instanceof Error ? e.message : "Save failed" });
    }
  };

  const withdraw = async () => {
    try {
      if (typeof localStorage !== "undefined" && policy?.app?.id) {
        localStorage.removeItem(`aithoris_adult_${policy.app.id}`);
      }
    } catch {
      /* ignore */
    }
    if (preview) {
      emit("withdrawn", { preview: true });
      showNotice();
      return;
    }
    try {
      await client.withdraw({
        recordId: activeConsent?.recordId,
        principalId: principalId || activeConsent?.principalId,
      });
      activeConsent = null;
      priorGrants = null;
      emit("withdrawn");
      applyGrants(
        Object.fromEntries((policy?.purposes ?? []).map((p) => [p.id, Boolean(p.required)])),
        "withdraw",
      );
      showNotice();
    } catch (e) {
      emit("error", { message: e instanceof Error ? e.message : "Withdraw failed" });
    }
  };

  const getConsent = async () => {
    if (!principalId) return activeConsent;
    try {
      activeConsent = await client.getConsent(principalId);
      return activeConsent;
    } catch {
      return null;
    }
  };

  const checkVersion = async () => {
    const pid = principalId || options.principalId?.trim() || "";
    if (!pid) return { needsUi: true, latestVersion: policy?.version ?? null };
    const resolved = await client.resolveConsent(pid);
    if (resolved.server) activeConsent = resolved.server;
    if (resolved.needsUi && (options.autoShow ?? true)) showNotice();
    return { needsUi: resolved.needsUi, latestVersion: resolved.latestVersion };
  };

  const mergeAnonymous = async (knownPrincipalId: string) => {
    const anon = client.getStoredAnonymousPrincipal();
    if (!anon) return null;
    const merged = await client.mergeAnonymous(anon, knownPrincipalId);
    principalId = knownPrincipalId;
    emit("updated", { consent: merged, merged: true });
    return merged;
  };

  const destroy = () => {
    stopTranslationCatchupPoll();
    if (colorSchemeQuery && onColorSchemeChange) {
      colorSchemeQuery.removeEventListener?.("change", onColorSchemeChange);
    }
    hide();
    removeDataRightsLauncher();
    removePreferencesLauncher();
    privacyMount?.unmount();
    privacyMount = null;
    if (options.onEvent) listeners.delete(options.onEvent);
    if (active === instance) active = null;
  };

  const instance: Instance = {
    showNotice: () => showNotice("banner"),
    showPreferences,
    openDataRightsRequest,
    hide,
    getConsent,
    withdraw,
    checkVersion,
    mergeAnonymous,
    destroy,
    requestOtp: (pid, idType) => client.requestOtp(pid, idType),
    loginWithOtp: (pid, otp, idType) => client.loginWithOtp(pid, otp, idType),
    logoutPrivacy: () => client.logoutPrivacy(),
    getPrivacyDashboard: () => client.getPrivacyDashboard(),
    getConsentHistory: () => client.getConsentHistory(),
    getActivityTimeline: () => client.getActivityTimeline(),
    getRequests: () => client.getRequests(),
    getRequestStatus: (id) => client.getRequestStatus(id),
    updatePreferences: (body) => client.updatePreferences(body),
    withdrawConsent: (recordId) => client.withdrawConsent(recordId),
    requestAccountDeletion: (body) => client.requestAccountDeletion(body),
    requestDataExport: (body) => client.requestDataExport(body),
    raiseComplaint: (body) => client.raiseComplaint(body),
    requestCorrection: (body) => client.requestCorrection(body),
    mountPrivacyDashboard: (el, opts) => {
      privacyMount?.unmount();
      privacyMount = mountPrivacyDashboard(client, {
        container: el,
        principalId: opts?.principalId ?? principalId,
        identifierType: opts?.identifierType,
        primaryColor: opts?.primaryColor ?? theme.primaryColor,
        theme: opts?.theme ?? {
          primaryColor: opts?.primaryColor ?? theme.primaryColor,
          logoUrl: theme.logoUrl,
          theme: theme.theme,
        },
        onClose: opts?.onClose,
      });
      return privacyMount;
    },
    unmountPrivacyDashboard: () => {
      privacyMount?.unmount();
      privacyMount = null;
    },
    setPreview: (next) => {
      if (!preview) return;
      if ("theme" in next) previewTheme = next.theme ?? null;
      if ("bannerOptions" in next) {
        previewBanner = next.bannerOptions ?? null;
        applyPreviewBanner();
      }
      if (root) showNotice(currentMode, { animate: false });
      else {
        ensureDataRightsLauncher();
        ensurePreferencesLauncher();
      }
    },
  };
  active = instance;

  try {
    // Prefer single config endpoint for Site ID embeds; fall back to legacy policy GET.
    let loaded: ConsentSdkPolicy | null = null;
    if (/^cmp_(live|test)_/i.test(appId)) {
      try {
        loaded = await client.getConfig({ language: selectedLanguage || undefined });
      } catch {
        loaded = null;
      }
    }
    if (!loaded) {
      const result = await client.getPolicy();
      loaded = result.policy;
    }
    if (!loaded) throw new Error("No published policy");
    masterPolicy = loaded;
    publishedBanner = loaded.bannerOptions ?? undefined;
    const readyLangs = readySelectorLanguages(loaded.localization);
    let storedLang = "";
    try {
      storedLang = preview ? "" : (localStorage.getItem(langStorageKey(appId)) ?? "");
    } catch {
      storedLang = "";
    }
    const configuredDefault = (loaded.bannerOptions?.defaultLanguage ?? "").toLowerCase();
    selectedLanguage =
      selectedLanguage ||
      (storedLang && readyLangs.includes(storedLang) ? storedLang : "") ||
      (configuredDefault && readyLangs.includes(configuredDefault) ? configuredDefault : "") ||
      loaded.languageSelected ||
      loaded.localization?.defaultLanguage ||
      "en";
    let bo = loaded.bannerOptions ?? null;
    if (bo?.position === "top" || bo?.position === "bottom" || bo?.position === "center") {
      position = bo.position;
    }
    publishedPosition = position;
    if (previewBanner) {
      applyPreviewBanner();
      loaded = masterPolicy;
      bo = masterPolicy.bannerOptions ?? null;
    }
    if (bo?.theme === "auto" && typeof window !== "undefined" && window.matchMedia) {
      colorSchemeQuery = window.matchMedia("(prefers-color-scheme: dark)");
      onColorSchemeChange = () => {
        if (root) showNotice(currentMode, { animate: false });
      };
      colorSchemeQuery.addEventListener?.("change", onColorSchemeChange);
    }
    const autoShow = (options.autoShow ?? true) && bo?.enabled !== false;
    const visitorState = normalizeVisitorState(bo?.defaultVisitorState ?? theme.defaultVisitorState);
    policy = applyTranslationOverlay(loaded, selectedLanguage);
    // Merge server branding into init theme, but never freeze notice copy —
    // language switches update policy.theme and must win via themeForUi().
    const {
      bannerHeadline: _omitHeadline,
      bannerBody: _omitBody,
      ...serverBranding
    } = policy.theme ?? {};
    void _omitHeadline;
    void _omitBody;
    Object.assign(theme, serverBranding);

    const beforeChoice = () => {
      const { activeBeforeChoice } = visitorGrants();
      applyGrants(
        Object.fromEntries(activeBeforeChoice.map((id) => [id, true])),
        "before_choice",
      );
    };

    if (principalId) {
      const resolved = await client.resolveConsent(principalId);
      let needsUi = resolved.needsUi;
      if (resolved.server?.isActive) {
        priorGrants = { ...resolved.server.purposes };
      } else if (resolved.cache?.purposes) {
        priorGrants = { ...resolved.cache.purposes };
      }
      if (resolved.server?.isActive) activeConsent = resolved.server;
      // `remember`: an earlier choice survives policy version bumps unless new purposes appeared.
      if (
        needsUi &&
        visitorState === "remember" &&
        resolved.server?.isActive &&
        !rememberNeedsPrompt(policy.purposes ?? [], priorGrants)
      ) {
        needsUi = false;
      }
      if (needsUi && visitorState === "remember" && resolved.server?.isActive) {
        // Re-prompt with prior toggles; old record is not treated as consent to new purposes.
        activeConsent = null;
      }
      emit("loaded", { consent: resolved.server, needsUi });
      if (activeConsent && !needsUi) applyGrants(activeConsent.purposes ?? {}, "restore");
      else beforeChoice();
      if (autoShow && needsUi) showNotice();
      else {
        ensureDataRightsLauncher();
        ensurePreferencesLauncher();
      }
    } else {
      const cacheHint = readConsentCache(appId, "anonymous");
      void cacheHint;
      emit("loaded", { needsUi: true });
      beforeChoice();
      if (autoShow) showNotice();
      else ensureDataRightsLauncher();
    }

    // Poll until PUBLISHED translations arrive so language switch gets real copy.
    startTranslationCatchupPoll();
  } catch (e) {
    emit("error", { message: e instanceof Error ? e.message : "Init failed" });
    throw e;
  }

  return instance;
}

function bootFromDom() {
  try {
    const script = findBootScript();
    const siteId = script?.getAttribute("data-site-id")?.trim();
    if (!siteId || active) return;
    const apiFromAttr = script?.getAttribute("data-api-base")?.trim();
    let apiBaseUrl = apiFromAttr;
    if (!apiBaseUrl && script?.src) {
      try {
        apiBaseUrl = new URL(script.src, window.location.href).origin;
      } catch {
        apiBaseUrl = undefined;
      }
    }
    void AithorisConsent.init({
      siteId,
      apiBaseUrl,
      appBaseUrl: script?.getAttribute("data-app-base")?.trim() || undefined,
      autoShow: script?.getAttribute("data-auto-show") !== "false",
    }).catch(() => {
      /* errors already emitted */
    });
  } catch {
    /* ignore boot errors */
  }
}

const AithorisConsent = {
  init,
  needsReconsent,
  version: COMPALLY_CONSENT_SDK_VERSION,
  on(handler: ConsentSdkEventHandler) {
    listeners.add(handler);
    return () => listeners.delete(handler);
  },
  getInstance: () => active,
  /**
   * Mount Privacy Dashboard without requiring banner init / published policy.
   * Use on org websites: Privacy & Consent nav → this mount.
   */
  mountPrivacyDashboard(
    el: HTMLElement,
    options: {
      appId?: string;
      siteId?: string;
      apiBaseUrl: string;
      principalId?: string;
      identifierType?: string;
      primaryColor?: string;
      theme?: import("./privacy-dashboard-mount").PrivacyDashboardTheme;
      onClose?: () => void;
    },
  ) {
    if (!el) throw new Error("mountPrivacyDashboard requires a container element");
    const appId = (options.siteId ?? options.appId)?.trim();
    const apiBaseUrl = resolveApiBase(options.apiBaseUrl);
    if (!appId) throw new Error("mountPrivacyDashboard requires siteId (or appId)");
    if (!apiBaseUrl) throw new Error("mountPrivacyDashboard requires apiBaseUrl");
    privacyMount?.unmount();
    const client = new ConsentSdkClient({
      apiBaseUrl,
      appId,
      sdkVersion: COMPALLY_CONSENT_SDK_VERSION,
    });
    privacyMount = mountPrivacyDashboard(client, {
      container: el,
      principalId: options.principalId,
      identifierType: options.identifierType,
      primaryColor: options.primaryColor,
      theme: options.theme ?? { primaryColor: options.primaryColor },
      onClose: options.onClose,
    });
    return privacyMount;
  },
  unmountPrivacyDashboard() {
    privacyMount?.unmount();
    privacyMount = null;
  },
};

/** @deprecated Use AithorisConsent */
const CompallyConsent = AithorisConsent;

declare global {
  interface Window {
    AithorisConsent: typeof AithorisConsent;
    /** @deprecated Use AithorisConsent */
    CompallyConsent: typeof AithorisConsent;
  }
}

if (typeof window !== "undefined") {
  window.AithorisConsent = AithorisConsent;
  window.CompallyConsent = AithorisConsent;
  // HTML one-line install: <script … data-site-id="cmp_live_…"> — no manual init().
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootFromDom, { once: true });
  } else {
    bootFromDom();
  }
}

export { AithorisConsent, CompallyConsent };
export default AithorisConsent;
